import axios from 'axios';
import { BASE_URL } from '../Constants/Api';

const API_URL = BASE_URL;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach Authorization token from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Fetch list of employees
export const getEmployees = async () => {
  try {
    const response = await api.get('/sales/manageemployee');
    return response.data.content || [];
  } catch (error) {
    console.error('Failed to fetch employees:', error);
    throw new Error('Failed to fetch employees');
  }
};

// Fetch list of executives (submittedBy)
export const fetchExecutives = async () => {
  try {
    const response = await api.get('/user/names');
    return response.data || [];
  } catch (error) {
    console.error('Failed to fetch executives:', error);
    throw new Error('Failed to fetch executives');
  }
};

// Get all clients (with optional search)
export const getClients = async (search = "") => {
  try {
    let url = '/sales/mktgemployees';
    if (search && search.trim() !== "") {
      url += `?search=${encodeURIComponent(search)}`;
    }
    const response = await api.get(url);
    return response.data || [];
  } catch (error) {
    console.error('Failed to fetch clients:', error);
    throw new Error('Failed to fetch clients');
  }
};

// In Services/salesapis.js
export const addClient = async (clientData) => {
    try {
      const response = await api.post('/sales/mktgemployees', clientData);
      return response.data;
    } catch (error) {
      console.error('Failed to add client:', error);
      throw new Error('Failed to add client');
    }
  };

// Get client by ID
export const getClientById = async (id) => {
  try {
    const response = await api.get(`/sales/mktgemployees/${id}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch client by ID:', error);
    throw new Error('Failed to fetch client by ID');
  }
};

// Update client by ID
export const updateClient = async (id, clientData) => {
  try {
    if (!id) {
      throw new Error('Client ID is required');
    }
    
    const response = await api.put(`/sales/mktgemployees/${id}`, clientData);
    if (!response.data) {
      throw new Error('No data received from server');
    }
    return response.data;
  } catch (error) {
    console.error('Failed to update client:', error);
    throw error.response?.data?.message || error.message || 'Failed to update client';
  }
};

export const getFilteredSubmissions = async ({ company, employees, submittedBy, fromDate, toDate, page = 0, size = 10, search = "", stsCd = "A" }) => { 
  const params = new URLSearchParams(); 
  
  // Only append parameters if they have valid values
  if (company) params.append("company", company); 
  if (employees) params.append("employees", employees); 
  if (submittedBy && submittedBy !== "all") params.append("submittedBy", submittedBy); 
  if (fromDate) params.append("fromDate", fromDate); 
  if (toDate) params.append("toDate", toDate); 
  params.append("page", page);
  params.append("size", size);
  if (search && search.trim() !== "") params.append("search", search);
  
  // Add status filter - default to "A" (Active) if not specified
  if (stsCd && stsCd !== "all") {
    params.append("stsCd", stsCd);
  }
  
  try { 
    const response = await api.get(`/sales/submissions?${params.toString()}`); 
    if (!response.data || (Array.isArray(response.data) && response.data.length === 0) || (response.data.content && response.data.content.length === 0)) {
      // If no data or empty array/content, return a specific message
      return { data: [], message: response.data.message || "No data found.", totalElements: 0, totalPages: 0 };
    }
    // If response.data is paginated, return content and total count
    if (response.data.content) {
      return {
        data: response.data.content,
        message: response.data.message,
        totalElements: response.data.totalElements || response.data.totalCount || 0,
        totalPages: response.data.totalPages || 0,
      };
    }
    // Otherwise, fallback to array
    return { data: Array.isArray(response.data) ? response.data : [], message: response.data.message, totalElements: response.data.length || 0, totalPages: 1 };
  } catch (error) { 
    console.error("Failed to fetch filtered submissions:", error); 
    // Return an empty array on error, with an error message
    return { data: [], message: error.response?.data?.message || "Failed to fetch submissions.", totalElements: 0, totalPages: 0 }; 
  } 
};

// Get submissions by user with pagination for reports
export const getSubmissionsByUser = async (page = 0, size = 10, timeframe = null, fromDate = null, toDate = null, submittedBy = null) => {
  try {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('size', size);
    
    if (timeframe) {
      params.append('timeFrame', timeframe);
    }
    
    if (fromDate) {
      // Convert YYYY-MM-DD to MM-DD-YYYY format
      const date = new Date(fromDate);
      const formattedFromDate = `${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}-${date.getFullYear()}`;
      params.append('fromDate', formattedFromDate);
    }
    
    if (toDate) {
      // Convert YYYY-MM-DD to MM-DD-YYYY format
      const date = new Date(toDate);
      const formattedToDate = `${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}-${date.getFullYear()}`;
      params.append('toDate', formattedToDate);
    }
    
    // Always send submittedBy parameter - "All" for default/empty, or the selected name
    const submittedByValue = submittedBy && submittedBy.trim() !== '' ? submittedBy : 'All';
    params.append('submittedBy', submittedByValue);
    
    const response = await api.get(`/sales/submissions/by-user?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch submissions by user:', error);
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch submissions by user');
  }
};

// Get submissions by user for dashboard (cards and charts) - no pagination
export const getSubmissionsByUserForDashboard = async (timeframe = null, fromDate = null, toDate = null, submittedBy = null) => {
  try {
    const params = new URLSearchParams();
    params.append('size', 'All');
    
    if (timeframe) {
      params.append('timeFrame', timeframe);
    }
    
    if (fromDate) {
      // Convert YYYY-MM-DD to MM-DD-YYYY format
      const date = new Date(fromDate);
      const formattedFromDate = `${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}-${date.getFullYear()}`;
      params.append('fromDate', formattedFromDate);
    }
    
    if (toDate) {
      // Convert YYYY-MM-DD to MM-DD-YYYY format
      const date = new Date(toDate);
      const formattedToDate = `${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}-${date.getFullYear()}`;
      params.append('toDate', formattedToDate);
    }
    
    // Always send submittedBy parameter - "All" for default/empty, or the selected name
    const submittedByValue = submittedBy && submittedBy.trim() !== '' ? submittedBy : 'All';
    params.append('submittedBy', submittedByValue);
    
    const response = await api.get(`/sales/submissions/by-user?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch submissions by user for dashboard:', error);
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch submissions by user for dashboard');
  }
};

// Fetch total consultants count
export const getTotalConsultantsCount = async () => {
  try {
    const response = await api.get('/sales/manageemployee', {
      params: { stsCd: 'A' }
    });
    return response.data.totalElements || 0;
  } catch (error) {
    console.error('Failed to fetch total consultants count:', error);
    throw new Error('Failed to fetch total consultants count');
  }
};

// Fetch users with zero submissions
export const getUsersWithZeroSubmissions = async () => {
  try {
    const response = await api.get('/sales/names/submissions=0?role=users');
    return response.data || [];
  } catch (error) {
    console.error('Failed to fetch users with zero submissions:', error);
    // Throw backend error message if present
    throw new Error(error.response?.data?.message || 'No users found with zero submissions');
  }
};

// Fetch consultants (manageemployees) with zero submissions
export const getConsultantsWithZeroSubmissions = async () => {
  try {
    const response = await api.get('/sales/names/submissions=0?role=manageemployees');
    return response.data || [];
  } catch (error) {
    console.error('Failed to fetch consultants with zero submissions:', error);
    // Throw backend error message if present
    throw new Error(error.response?.data?.message || 'No consultants found with zero submissions');
  }
};
